// WHY (#700): destinations multiselect for the draft editor. Replaces
// the legacy freeform from/to inputs in `DraftBasicsCard`. Loads the
// arrival-city catalog from `/api/arrival-cities`, defaults a brand-new
// scratchpad to the row with `isDefault = true`, and lets the logist
// add/remove additional cities from a dropdown of the remaining active
// rows. Re-renders sync the store via `setDraftDestinations`.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Select } from '../../../ui/index.js';
import { useActiveArrivalCities, type ActiveArrivalCity } from '../../../catalog/index.js';
import { useRequestsStore } from '../store.js';
import type { DraftScratchpad } from '../types.js';

export function DestinationsField({ d }: { d: DraftScratchpad }) {
  const { t } = useTranslation(['requests']);
  const setDestinations = useRequestsStore((s) => s.setDraftDestinations);
  const [picker, setPicker] = useState('');

  // Active cities catalog — small (typically < 50 rows) so a single
  // unbounded read is fine. The arrival-cities admin page uses the same
  // shape; share the cache key so an edit there invalidates this too.
  const citiesQuery = useActiveArrivalCities();

  const allCities = useMemo<ActiveArrivalCity[]>(() => citiesQuery.data ?? [], [citiesQuery.data]);
  const byId = useMemo(() => new Map(allCities.map((c) => [c.id, c])), [allCities]);

  // WHY (#700 review F-002): seed the default city exactly once per
  // mount, gated on a ref so a user who removes the only chip is NOT
  // bounced back to the seeded default. Without the ref, the empty
  // destinations effect would re-fire and silently undo the removal.
  // A re-opened persisted draft already has its destinations hydrated
  // — the length check skips the seed in that case.
  const seededRef = useRef(false);
  useEffect(() => {
    if (seededRef.current) return;
    if (d.destinations.length > 0) {
      seededRef.current = true;
      return;
    }
    if (allCities.length === 0) return;
    const defaultCity = allCities.find((c) => c.isDefault) ?? allCities[0];
    if (defaultCity) {
      setDestinations([{ arrivalCityId: defaultCity.id, sortOrder: 0 }]);
      seededRef.current = true;
    }
  }, [d.destinations.length, allCities, setDestinations]);

  const selectedIds = useMemo(
    () => new Set(d.destinations.map((dest) => dest.arrivalCityId)),
    [d.destinations],
  );
  const remaining = useMemo(
    () => allCities.filter((c) => !selectedIds.has(c.id)),
    [allCities, selectedIds],
  );

  function addCity(id: string): void {
    if (!id || selectedIds.has(id)) return;
    const next = [...d.destinations, { arrivalCityId: id, sortOrder: d.destinations.length }];
    setDestinations(next);
    setPicker('');
  }

  function removeCity(id: string): void {
    const next = d.destinations
      .filter((dest) => dest.arrivalCityId !== id)
      .map((dest, idx) => ({ arrivalCityId: dest.arrivalCityId, sortOrder: idx }));
    setDestinations(next);
  }

  return (
    <div data-testid="destinations-field">
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        {d.destinations.length === 0 ? (
          <span style={{ color: 'var(--fg-3)', fontSize: 13 }}>
            {t('requests:draft.destinations.empty')}
          </span>
        ) : (
          d.destinations.map((dest) => {
            const city = byId.get(dest.arrivalCityId);
            const label = city ? city.name : dest.arrivalCityId;
            return (
              <span
                key={dest.arrivalCityId}
                data-testid={`destination-chip-${dest.arrivalCityId}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 8px',
                  border: '1px solid var(--border-1)',
                  borderRadius: 12,
                  fontSize: 13,
                  background: 'var(--bg-2)',
                  color: 'var(--fg-1)',
                }}
              >
                {label}
                <IconButton
                  icon="x"
                  size={20}
                  label={t('requests:draft.destinations.remove', { name: label })}
                  onClick={() => removeCity(dest.arrivalCityId)}
                />
              </span>
            );
          })
        )}
      </div>
      {remaining.length > 0 && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <Select
            value={picker}
            onChange={setPicker}
            options={[
              { value: '', label: t('requests:draft.destinations.pickerPlaceholder') },
              ...remaining.map((c) => ({ value: c.id, label: c.name })),
            ]}
            placeholder={t('requests:draft.destinations.pickerPlaceholder')}
          />
          <Button onClick={() => addCity(picker)} disabled={!picker}>
            {t('requests:draft.destinations.add')}
          </Button>
        </div>
      )}
    </div>
  );
}
